/**
 * Las sugerencias del chat vacío, según la PANTALLA desde la que se abre.
 *
 * Sustituyen a los ejemplos fijos, cuyo primero («¿Qué sectores tienen mayor
 * riesgo IRM?») contestaba «no puedo» en casi todas las empresas: el IRM solo
 * funciona con el tope por producto relleno, y casi nadie lo tiene. Una
 * sugerencia es una promesa: aquí solo van preguntas que el asistente contesta
 * bien (el banco de ayuda las cubre) y que tienen que ver con lo que la persona
 * está mirando — en Equipo, cómo dar de alta; en Inventario, qué está bajo.
 *
 * Idiomas: TODO sale de los locales, en los nueve idiomas. Las de «cómo se
 * hace» y la del gasto del mes viven en `ipmAssistant.suggest.<clave>`; las de
 * datos que ya existían reutilizan `ipmAssistant.example*`. Antes las de ayuda
 * iban escritas aquí en siete idiomas y bambara y maninka caían a francés: la
 * sugerencia salía en un idioma y la respuesta del asistente en otro. Ahora
 * `npm run i18n:parity` las vigila como cualquier otro texto, y las de bm/man
 * (traducción automática) están apuntadas para revisión humana en
 * scripts/bm-man-machine-translated.tsv.
 */

import type { AssistantModeId } from "./assistantModes";

export type SuggestionKind = "help" | "data";

export interface Suggestion {
  key: string;
  /** Clave i18n del texto: es también lo que se manda como pregunta al pulsar. */
  i18nKey: string;
  kind: SuggestionKind;
}

/** Preguntas de «cómo se hace»: las contesta la guía (get_app_help). */
const HELP_KEYS = [
  "teamAdd",
  "teamPassword",
  "teamScope",
  "invoiceScan",
  "photoLabel",
  "voiceTask",
  "moveTask",
  "reportPest",
  "createTreatment",
  "historic",
  "fixLog",
  "fieldNotebook",
  "mapSample",
  "cycle",
  "reportsWhere",
  "changeEmail",
  "language",
  "billing",
] as const;

/** Preguntas de DATOS con su propia clave (la contestan las herramientas, no la guía). */
const DATA_SUGGEST_KEYS = ["monthlyCosts"] as const;

/** Las de datos que ya existían: claves `ipmAssistant.example*`, en los nueve locales desde antes. */
const DATA: Record<string, string> = {
  pestSummary: "ipmAssistant.example2",
  pendingTasks: "ipmAssistant.example3",
  lowStock: "ipmAssistant.example4",
  spiderTreatments: "ipmAssistant.example5",
};

/**
 * Sugerencias que, con su modo disponible para la empresa, ABREN el modo en vez
 * de preguntar (CQ4b, PLAN §3): la del cuaderno de /spray-logs entra en
 * «@ Cuaderno de campo». Sin el modo (interruptor apagado) siguen siendo la
 * pregunta de ayuda de siempre.
 */
const SUGGESTION_MODE: Record<string, AssistantModeId> = { fieldNotebook: "cuaderno" };

export function suggestionMode(key: string): AssistantModeId | null {
  return SUGGESTION_MODE[key] ?? null;
}

/** Clave i18n de una sugerencia con texto propio. */
export const suggestI18nKey = (key: string): string => `ipmAssistant.suggest.${key}`;

/** Pantalla → sugerencias. Gana el PRIMER prefijo que case. */
const BY_ROUTE: Array<[prefix: string, keys: string[]]> = [
  ["/settings/team", ["teamAdd", "teamPassword", "teamScope"]],
  ["/teams", ["teamAdd", "teamPassword", "pendingTasks"]],
  ["/inventory", ["lowStock", "invoiceScan", "photoLabel"]],
  ["/planner", ["pendingTasks", "voiceTask", "moveTask"]],
  ["/pests", ["pestSummary", "reportPest", "createTreatment"]],
  ["/spray-logs", ["historic", "fixLog", "fieldNotebook"]],
  ["/blocks", ["mapSample", "pestSummary", "cycle"]],
  ["/analytics", ["monthlyCosts", "reportsWhere", "pestSummary"]],
  ["/reports", ["monthlyCosts", "reportsWhere", "pestSummary"]],
  ["/settings", ["changeEmail", "language", "billing"]],
];
const DEFAULT_KEYS = ["pendingTasks", "lowStock", "voiceTask"];

export const SUGGESTION_KEYS = {
  help: [...HELP_KEYS] as string[],
  data: [...Object.keys(DATA), ...DATA_SUGGEST_KEYS],
};

/** Todas las claves i18n que usan las sugerencias (para el guardián de los nueve locales). */
export const SUGGESTION_I18N_KEYS: string[] = [
  ...HELP_KEYS.map(suggestI18nKey),
  ...DATA_SUGGEST_KEYS.map(suggestI18nKey),
  ...Object.values(DATA),
];

const isHelp = (key: string) => (HELP_KEYS as readonly string[]).includes(key);

export function suggestionsForRoute(pathname: string): Suggestion[] {
  const keys = BY_ROUTE.find(([p]) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}?`))?.[1] ?? DEFAULT_KEYS;
  return keys.map((key): Suggestion =>
    DATA[key]
      ? { key, i18nKey: DATA[key], kind: "data" }
      : { key, i18nKey: suggestI18nKey(key), kind: isHelp(key) ? "help" : "data" },
  );
}

/* ── La pantalla de la que VIENES ─────────────────────────────────────────
   En el teléfono el chat es una hoja encima de la pantalla, así que
   `pathname` ya es «lo que estás mirando». En el ordenador el chat es su propia
   página (/advanced) y el pathname era siempre ese: las sugerencias salían
   siempre las de por defecto. Se recuerda la última pantalla de la app que no
   es el asistente (la apunta AssistantButton, que está montado en todas) y se
   usa esa. En memoria y, por si se recarga /advanced, en sessionStorage. */

const LAST_ROUTE_KEY = "ulture.assistant.lastRoute";
let lastRoute: string | null = null;

const isAssistantRoute = (p: string) => p === "/advanced" || p.startsWith("/advanced/") || p.startsWith("/advanced?");

/** Apunta la pantalla actual si no es la del asistente. */
export function rememberAppRoute(pathname: string): void {
  if (!pathname || isAssistantRoute(pathname)) return;
  lastRoute = pathname;
  try {
    sessionStorage.setItem(LAST_ROUTE_KEY, pathname);
  } catch { /* modo privado: basta la memoria */ }
}

/** La ruta que decide las sugerencias: la actual o, en la página del asistente, la última de la app. */
export function suggestionRoute(pathname: string): string {
  if (!isAssistantRoute(pathname)) return pathname;
  if (lastRoute) return lastRoute;
  try {
    return sessionStorage.getItem(LAST_ROUTE_KEY) ?? pathname;
  } catch {
    return pathname;
  }
}

/**
 * El texto en el idioma de la interfaz: lo da i18next con su propio respaldo.
 * `language` ya no decide nada (antes elegía la columna de una tabla escrita
 * aquí); se conserva en la firma para no tocar a quien la llama.
 */
export function suggestionText(s: Suggestion, _language: string, t: (k: string) => string): string {
  return t(s.i18nKey);
}
